import React, { useMemo } from 'react';
import { BaseSelector, BaseSelectorProps } from '../BaseSelector';
import { Option } from '../CustomDropdown';
import { useQuestionTypeRefinement } from './useQuestionTypeRefinement';
import { QUESTION_TYPES } from './GenericQuestionTypeSelector';
import CustomOptionWithCount from '../CustomOptionWithCount';

export type GenericQuestionTypeSelectorWithCountsProps = Omit<
  BaseSelectorProps,
  'label' | 'options'
> & {
  // If true, will attempt to use Algolia refinement context for counts
  enableCounts?: boolean;
  // Manual override for count data (useful for testing or custom data sources)
  countData?: Array<{ value: string; count: number }>;
};

/**
 * Enhanced GenericQuestionTypeSelector that displays counts from Algolia refinements
 */
export const GenericQuestionTypeSelectorWithCounts: React.FC<GenericQuestionTypeSelectorWithCountsProps> =
  ({ enableCounts = false, countData, ...props }) => {
    // Always call the hook at top level, but only use it if enableCounts is true
    const questionTypeRefinement = useQuestionTypeRefinement();

    const optionsWithCounts = useMemo((): Option[] => {
      // Start with base question types
      const baseOptions = [...QUESTION_TYPES];

      // If counts are not enabled, return base options
      if (!enableCounts) {
        return baseOptions;
      }

      // Add counts from manual data if provided
      if (countData) {
        return baseOptions.map((option) => {
          const countItem = countData.find((item) => item.value === option.value);
          return {
            ...option,
            count: countItem?.count,
          };
        });
      }

      // Add counts from refinement context if available
      if (questionTypeRefinement && enableCounts) {
        return baseOptions.map((option) => {
          const count = questionTypeRefinement.getCountForValue(option.value);
          return {
            ...option,
            count: count > 0 ? count : 0,
          };
        });
      }

      // Fallback to base options if no count data available
      return baseOptions;
    }, [enableCounts, countData, questionTypeRefinement]);

    // Custom label functions to show counts when available
    const optionLabel = (option: Option) => {
      if (enableCounts && option.count !== undefined) {
        return (
          <CustomOptionWithCount value="" label={option.label} count={option.count} />
        );
      }
      return option.label;
    };

    const selectedLabel = (selected: Option) => `Question Type: ${selected.label}`;

    return (
      <BaseSelector
        {...props}
        label="Question Type"
        options={optionsWithCounts}
        optionLabel={optionLabel}
        selectedLabel={selectedLabel}
        tooltip="Filter by question type to find specific interaction styles"
        dataTestId="question-type-base-selector"
      />
    );
  };

// TODO: Remove this component prefix once we remove the old question type selector
export default GenericQuestionTypeSelectorWithCounts;
